import styled from "@emotion/styled";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  IconButton,
  Theme,
  TIcons,
  Typography,
  useTheme,
} from "@yuno/design-system-web";
import { useContext, useState } from "react";
import { ThemeContext } from "../../../context/theme-context";
import { Branding } from "../common/tools/branding";
import { PaymentForm } from "../common/tools/payment-form";
import { ThemeSelector } from "../common/tools/selectors/theme-selector";
import { useTranslation } from "react-i18next";

const AccordionTitle = styled(Typography)<{ theme?: Theme }>`
  display: flex;
  align-items: center;

  & svg {
    fill: ${({ theme }) => theme.palette.grey[5]};
  }

  & p {
    ${({ theme }) => theme.typography.small.regular};
    margin-left: 12px;
  }
`;

const TitleBold = styled(Typography)<{ theme?: Theme }>`
  ${({ theme }) => theme.typography.small.bold};
  margin-left: 10px;
  color: ${({ theme }) => theme.palette.neutral.black};
`;

const TitleContainer = styled.div<{ theme?: Theme }>`
  padding-left: 10px;
  padding-right: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
  padding-bottom: 20px;
  display: flex;
  align-items: center;
`;

const ToolsContainer = styled.div(() => ({
  "& .MuiAccordion-rounded:before": {
    display: "none",
  },
}));

const AccordionSummaryStyled = styled(AccordionSummary)<{ theme?: Theme }>`
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
`;

const AccordionDetailsStyled = styled(AccordionDetails)<{ theme?: Theme }>`
  padding: 14px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
`;

const tools = [
  {
    id: "THEME",
    name: "styles.toolsOpt.theme",
    icon: "Palette",
    active: true,
    component: <ThemeSelector />,
  },
  {
    id: "ORIENTATION",
    name: "styles.toolsOpt.orientation",
    icon: "DeviceMobileCamera",
    active: false,
    component: () => <></>,
  },
  {
    id: "BRANDING",
    name: "styles.toolsOpt.branding",
    icon: "PaintBrush",
    active: false,
    component: <Branding />,
  },
  {
    id: "CHECKOUT_FONT",
    name: "styles.toolsOpt.font",
    icon: "TextAa",
    active: false,
    component: () => <></>,
  },
  {
    id: "SHOPPING_CART",
    name: "styles.toolsOpt.cart",
    icon: "ShoppingCartSimple",
    active: false,
    component: () => <></>,
  },
  {
    id: "PAYMENT_FORM",
    name: "styles.toolsOpt.payments",
    icon: "Textbox",
    active: true,
    component: <PaymentForm />,
  },
];

export const ToolsCheckout = ({ onBack }) => {
  const theme: Theme = useTheme();
  const { setThemeSelected } = useContext(ThemeContext);
  const [expandedTool, setExpandedTool] = useState("");
  const { t } = useTranslation();

  const handleChange = (tool) => {
    if (expandedTool === tool) {
      setExpandedTool("");
    } else {
      setExpandedTool(tool);
    }

    if (tool !== "THEME") {
      setThemeSelected("custom");
    }
  };

  return (
    <ToolsContainer>
      <TitleContainer>
        <IconButton sx={{ padding: 0 }} disableRipple={true} onClick={onBack}>
          <Icon
            size="20px"
            name="ArrowLeft"
            weight="thin"
            color={theme.palette.grey[5]}
          />
        </IconButton>
        <TitleBold>{t("styles.customTitle")}</TitleBold>
      </TitleContainer>
      {tools
        .filter((tool) => tool.active)
        .map((tool, key) => (
          <Accordion
            key={key}
            elevation={0}
            disableGutters
            onChange={handleChange.bind(null, tool.id)}
            expanded={tool.id === expandedTool}
          >
            <AccordionSummaryStyled
              expandIcon={<Icon name="CaretDown" />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <AccordionTitle>
                <Icon
                  name={tool.icon as TIcons}
                  size="16px"
                  color={theme.palette.grey[5]}
                />
                <Typography>{t(tool.name)}</Typography>
              </AccordionTitle>
            </AccordionSummaryStyled>
            {tool?.component && (
              <AccordionDetailsStyled>
                <>{tool.component}</>
              </AccordionDetailsStyled>
            )}
          </Accordion>
        ))}
    </ToolsContainer>
  );
};
